import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/shared/ui/button";
import { Copy, ChevronDown } from "lucide-react";
import { useTranslation } from "@/shared/i18n";

type Platform = "ios" | "android" | "desktop";

function detectPlatform(): Platform {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  }

  if (/android/.test(userAgent)) {
    return "android";
  }

  return "desktop";
}

function getStoreUrl(platform: Platform): string {
  switch (platform) {
    case "ios":
      return "https://apps.apple.com/app/likeappro/id1468108552";
    case "android":
      return "https://play.google.com/store/apps/details?id=com.likeappro.app";
    default:
      return "https://www.likeappro.com/";
  }
}

export default function StoreRedirectPage() {
  const { t } = useTranslation("store-redirect");
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [platform] = useState<Platform>(() => detectPlatform());

  const deepLink = searchParams.get("deep_link") || "";

  const handleCopy = async () => {
    if (!deepLink) return;

    try {
      await navigator.clipboard.writeText(deepLink);
      setCopied(true);

      setTimeout(() => {
        const storeUrl = getStoreUrl(platform);
        window.location.href = storeUrl;
      }, 1500);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = deepLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);

      setTimeout(() => {
        const storeUrl = getStoreUrl(platform);
        window.location.href = storeUrl;
      }, 1500);
    }
  };

  const handleSkip = () => {
    const storeUrl = getStoreUrl(platform);
    window.location.href = storeUrl;
  };

  useEffect(() => {
    if (platform === "desktop") {
      window.location.href = getStoreUrl("desktop");
    }
  }, [platform]);

  if (platform === "desktop") {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-white flex flex-col items-center justify-center px-6 py-12 overflow-auto">
      <div className="w-full max-w-sm flex flex-col items-center">
        <img
          src="/app-logo.png"
          alt="LikeAppro"
          className="h-20 mb-6 rounded-2xl"
        />

        <h1 className="text-2xl font-bold text-gray-900 mb-8">{t("title")}</h1>

        {deepLink && (
          <div className="w-full bg-gray-100 rounded-xl p-6 mb-6">
            <p className="text-gray-500 text-sm text-center mb-5">
              {t("copyDescription")}
            </p>

            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                <Copy className="w-5 h-5 text-gray-400" />
              </div>

              <div className="h-4 border-l border-dashed border-gray-300" />

              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                <img
                  src={platform === "ios" ? "/assets/icons/apple-store.svg" : "/assets/icons/google-play.svg"}
                  alt={platform === "ios" ? "App Store" : "Google Play"}
                  className="w-6 h-6"
                />
              </div>

              <div className="h-4 border-l border-dashed border-gray-300" />

              <ChevronDown className="w-5 h-5 text-gray-400" />
            </div>

            <p className="text-gray-500 text-sm text-center mt-3">
              {t("redirectDescription")}
            </p>
          </div>
        )}

        <p className="text-gray-400 text-xs mb-6">{t("poweredBy")}</p>

        <Button
          onClick={handleCopy}
          disabled={copied}
          className="w-full h-12 bg-[#D91657] hover:bg-[#D91657]/90 text-white font-medium text-base rounded-full disabled:opacity-70"
        >
          <Copy className="w-4 h-4 mr-2" />
          {copied ? t("copiedButton") : t("downloadButton")}
        </Button>

        <button
          onClick={handleSkip}
          className="mt-4 text-gray-400 hover:text-gray-500 text-sm"
        >
          {t("skipLink")}
        </button>
      </div>
    </div>
  );
}
